import React from 'react';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import {DeepPartial} from 'fishery';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {Experiment, ExperimentCollectionType} from 'iron/apps/experiments/src/types';

import {PageTrackingEvent, trackExperimentPageAction, TrackingEventSource} from '../tracking';
import {DEFAULT_CONFIDENCE_LEVEL, DEFAULT_CONTROL_KEY, DEFAULT_SAMPLE_SIZE} from '../utils';
import {useUpdateExperiment} from './update-experiment';

export function useDraftExperiment() {
  const experimentState = useExperimentsStore((state) => state.experimentState!);
  const draftExperiment = useExperimentsStore((state) => state.draftExperiment);
  const setDraftExperiment = useExperimentsStore((state) => state.setDraftExperiment);
  const updateExperiment = useUpdateExperiment();

  const newDraft = React.useCallback(() => {
    setDraftExperiment({
      settings: {},
    });
  }, [setDraftExperiment]);

  const newDraftFromCurrentState = React.useCallback(
    (source: TrackingEventSource) => {
      const draft = cloneDeep(experimentState);
      draft.settings.collectionMethod = draft.settings.collectionMethod ?? ExperimentCollectionType.EXPOSURE_EVENTS;
      draft.settings.confidenceLevel = draft.settings.confidenceLevel ?? DEFAULT_CONFIDENCE_LEVEL;
      draft.settings.controlKey = draft.settings.controlKey ?? DEFAULT_CONTROL_KEY;
      draft.settings.sampleSize = draft.settings.sampleSize ?? DEFAULT_SAMPLE_SIZE;
      setDraftExperiment(draft);
      trackExperimentPageAction({
        event: PageTrackingEvent.StartEditSettings,
        experiment: experimentState,
        source,
      });
    },
    [experimentState],
  );

  const updateDraft = React.useCallback(
    (updates: DeepPartial<Experiment>) => {
      // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
      const draft = merge({}, draftExperiment, updates);
      setDraftExperiment(draft);
    },
    [draftExperiment, setDraftExperiment],
  );

  const saveDraft = React.useCallback(
    (optimistic: boolean = true) => {
      updateExperiment(draftExperiment as Experiment, {updateHistory: false, optimistic});
      setDraftExperiment(undefined);
      trackExperimentPageAction({
        event: PageTrackingEvent.SaveEditSettings,
        experiment: experimentState,
      });
    },
    [draftExperiment, experimentState, updateExperiment],
  );

  const discardDraft = React.useCallback(() => {
    setDraftExperiment(undefined);
    trackExperimentPageAction({
      event: PageTrackingEvent.CancelEditSettings,
      experiment: experimentState,
    });
  }, [experimentState, setDraftExperiment]);

  return {
    newDraft,
    newDraftFromCurrentState,
    updateDraft,
    saveDraft,
    discardDraft,
  };
}
